import React from 'react';
import { Box, Typography, Button, Card, CardContent, Container, Alert } from '@mui/material';
import { ErrorOutline, RefreshRounded } from '@mui/icons-material';
import { BRAND } from '../theme/colors';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ error, info });
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error', error, info);
  }

  render() {
    if (this.state.hasError) {
      const { error, info } = this.state;
      return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <ErrorOutline sx={{ fontSize: 80, color: 'error.main', mb: 3, opacity: 0.8 }} />
            
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 2 }}>
              Oops! Something went wrong
            </Typography>
            
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, fontSize: '1.1rem' }}>
              We encountered an unexpected error. Don&apos;t worry, we&apos;ve logged it and will look into it.
            </Typography>

            {process.env.NODE_ENV === 'development' && (
            <Card sx={{
              width: '100%',
              mb: 4,
              bgcolor: '#fff3e0',
              border: '2px solid #ff9800',
              borderRadius: 2
            }}>
              <CardContent>
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Error Details (for debugging)
                  </Typography>
                </Alert>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    overflow: 'auto',
                    maxHeight: 200,
                    bgcolor: '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    color: 'error.main',
                    mb: 2
                  }}
                >
                  {error?.toString()}
                </Typography>
                {info && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontFamily: 'monospace',
                      bgcolor: '#f5f5f5',
                      p: 1.5,
                      borderRadius: 1,
                      color: '#666',
                      overflow: 'auto',
                      maxHeight: 150,
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word'
                    }}
                  >
                    {info.componentStack}
                  </Typography>
                )}
              </CardContent>
            </Card>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                onClick={() => window.location.reload()}
                sx={{
                  bgcolor: BRAND.green,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  '&:hover': { bgcolor: '#2d5a1e' }
                }}
                startIcon={<RefreshRounded />}
                size="large"
              >
                Reload Application
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => window.location.href = '/'}
                sx={{
                  borderColor: BRAND.green,
                  color: BRAND.green,
                  px: 4,
                  py: 1.5,
                  '&:hover': { borderColor: '#2d5a1e', bgcolor: '#f0f7f1' }
                }}
                size="large"
              >
                Go to Dashboard
              </Button>
            </Box>

            <Typography variant="caption" sx={{ mt: 4, color: 'text.secondary' }}>
              If this problem persists, please contact the administrator.
              {error && (
                <Typography component="span" variant="caption" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', color: 'error.main' }}>
                  {error.message || String(error)}
                </Typography>
              )}
            </Typography>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
